import { CenteredErrorMessage } from '@/components_deprecated/CenteredErrorMessage/CenteredErrorMessage'
import { FullPageLoader } from '@/components_deprecated/Loader'
import { CompanyContext } from '@/context/CompanyContext'
import UserContext from '@/context/UserContext'
import { BOOTSTRAP_QUERY } from '@/modules/authentication/queries'
import type { BootstrapQuery } from '@/modules/authentication/types.graphql'
import { NoCompanies } from '@/modules/companies'
import { useModuleConfigs } from '@/modules/companies/CompanySettings/hooks'
import { RegisterUser } from '@/modules/users/RegisterUser/RegisterUser'
import { ChangePassword } from '@/modules/users/user-settings/ChangePassword'
import { useQuery } from '@apollo/client'
import { useTranslate, UseFormProvider } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo } from 'react'
import { RouterProvider } from 'react-router'
import { NoAuthRoutes } from './NoAuthRoutes'
import { createAuthRouter } from './router'

// Create a new context which allows a user to switch company. Calling this
// will update the history, and refetch the bootstrap query
const SwitchCompanyContext = React.createContext<(arg: string) => void>(
  () => undefined
)

// The primary role of this component is to bootstrap
// all necessary information about the current user and company,
// and then add this to different contexts
export function Bootstrap() {
  const translations = useTranslate({
    genericError: 'errors.generic-error',
  })

  // The bootstrap query fetches the current user and the current company
  // the user is on, if any.
  const { loading, data, error, refetch } =
    useQuery<BootstrapQuery>(BOOTSTRAP_QUERY)

  // We need to load module configs early on
  const {
    loading: loadingModuleConfigs,
    hasAnyModule,
    refetch: refetchModuleConfigs,
  } = useModuleConfigs(!data?.me || !data?.currentCompany)

  useEffect(() => {
    if (
      !!data?.me &&
      !!data?.currentCompany &&
      !loadingModuleConfigs &&
      hasAnyModule
    ) {
      if (
        localStorage.getItem('FIRMADOK_REDIRECT_LOGIN') &&
        localStorage
          .getItem('FIRMADOK_REDIRECT_LOGIN')
          ?.includes(data?.currentCompany?.shortName ?? '') &&
        !data.me.isSuperuser
      ) {
        window.location.href =
          localStorage.getItem('FIRMADOK_REDIRECT_LOGIN') ?? '/'
        localStorage.removeItem('FIRMADOK_REDIRECT_LOGIN')
      }
    }
  }, [data?.currentCompany, data?.me, hasAnyModule, loadingModuleConfigs])

  const authRouter = useMemo(() => {
    const userId = data?.me?.id
    const companyShortName = data?.currentCompany?.shortName

    if (!userId || !companyShortName) return null

    return createAuthRouter(companyShortName, userId)
  }, [data?.currentCompany?.shortName, data?.me?.id])

  if (error)
    return (
      <>
        <CenteredErrorMessage message={translations.genericError} />
      </>
    )

  if (loading || data === undefined || loadingModuleConfigs)
    return <FullPageLoader />

  const me = data.me
  const currentCompany = data.currentCompany

  // If the user is not logged in, render routes for non-authenticated
  // visitors.
  if (me === null || me === undefined) return <NoAuthRoutes />

  // The user might need to either change password or do the initial registration
  if (!me.hasRegistered) return <RegisterUser user={me} />
  else if (me.needsPasswordChange) return <ChangePassword />

  // If the user is not an a company path, try to find a company
  // the user is associated with and navigate the user there
  if (currentCompany === null) {
    let userCompany = null

    // Try most recent company first if user is allowed to access it
    if (me.mostRecentCompany && me.mostRecentCompany.userHasAccess) {
      userCompany = me.mostRecentCompany
    }

    // Otherwise, try to find the first active company
    else if (me.companies.length > 0)
      userCompany = me.companies.find(c => c.userHasAccess)

    if (!userCompany) return <NoCompanies />

    window.history.pushState({}, '', '/c/' + userCompany.shortName)
    void refetch().then(() => refetchModuleConfigs())
    return <FullPageLoader />
  }

  if (!authRouter)
    return <CenteredErrorMessage message={translations.genericError} />

  // Render the primary internal view, wrapped in the company context
  // and the user context
  return (
    <SwitchCompanyContext.Provider
      value={(newCompany: string) => {
        window.history.pushState({}, '', '/c/' + newCompany)
        void refetch()
      }}
    >
      <CompanyContext.Provider value={currentCompany}>
        <UserContext.Provider value={me}>
          <UseFormProvider
            config={{ storeFormStateKeyPrefix: currentCompany.shortName }}
          >
            <RouterProvider
              key={currentCompany.shortName}
              router={authRouter}
            />
          </UseFormProvider>
        </UserContext.Provider>
      </CompanyContext.Provider>
    </SwitchCompanyContext.Provider>
  )
}
